import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SocialSidebar from '../components/layout/SocialSidebar';
import LoanApplicationModal from '../components/loans/LoanApplicationModal';
import { products, companyInfo } from '../data/mockData';
import { Phone, Mail, Download, CheckCircle, FileText, Users, Calculator, ArrowLeft, ArrowRight } from 'lucide-react';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="pt-[140px] pb-16">
          <div className="max-w-7xl mx-auto px-4 text-center py-20">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h1>
            <p className="text-gray-600 mb-8">The product you are looking for does not exist.</p>
            <Link to="/products" className="bg-[#2A9D8F] text-white px-6 py-3 rounded font-medium">
              View All Products
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Find related products
  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 3);

  const [showApplicationModal, setShowApplicationModal] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{product.title} - {companyInfo.name}</title>
        <meta name="description" content={product.shortDescription} />
        <meta property="og:title" content={`${product.title} - ${companyInfo.name}`} />
        <meta property="og:description" content={product.shortDescription} />
        <meta property="og:image" content={product.heroImage} />
      </Helmet>

      <Header />
      <SocialSidebar />

      <main>
        {/* Hero Section - Full Height */}
        <div className="relative h-[100svh] min-h-[550px] md:min-h-[650px]">
          <img
            src={product.heroImage}
            alt={product.title}
            className="w-full h-full object-cover brightness-125 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/15 via-transparent to-black/10" />
          <div className="absolute inset-0 flex items-center pt-[100px]">
            <div className="max-w-7xl mx-auto px-4 w-full">
              <nav className="text-white/70 text-sm mb-4">
                <Link to="/" className="hover:text-white">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/products" className="hover:text-white">Products</Link>
                <span className="mx-2">/</span>
                <span className="text-white">{product.title}</span>
              </nav>
              <span className="inline-block bg-white/20 text-white px-3 py-1 rounded text-sm mb-3">
                {product.category}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {product.title}
              </h1>
              <p className="text-white/90 text-lg max-w-2xl">
                {product.shortDescription}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions Bar */}
        <div className="bg-white shadow-md">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center space-x-6">
                <a href="#features" className="text-gray-600 hover:text-[#2A9D8F] font-medium">Features</a>
                <a href="#eligibility" className="text-gray-600 hover:text-[#2A9D8F] font-medium">Eligibility</a>
                <a href="#documents" className="text-gray-600 hover:text-[#2A9D8F] font-medium">Documents</a>
              </div>
              <div className="flex items-center space-x-4">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center text-[#2A9D8F] hover:underline"
                >
                  <Phone size={18} className="mr-2" />
                  Call Now
                </a>
                <button
                  onClick={() => setShowApplicationModal(true)}
                  className="bg-[#2A9D8F] text-white px-6 py-2 rounded font-medium hover:bg-[#0055a0] transition-colors"
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Features Section */}
              <section id="features" className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <CheckCircle size={28} className="text-[#2A9D8F] mr-3" />
                  Key Features
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 bg-[#2A9D8F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Eligibility Section */}
              <section id="eligibility" className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Users size={28} className="text-[#2A9D8F] mr-3" />
                  Eligibility Criteria
                </h2>
                <ul className="space-y-3">
                  {product.eligibility.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <ArrowRight size={18} className="text-[#2A9D8F] mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Documents Section */}
              <section id="documents" className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FileText size={28} className="text-[#2A9D8F] mr-3" />
                  Required Documents
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.documents.map((doc, index) => (
                    <div key={index} className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg">
                      <FileText size={20} className="text-[#2A9D8F]" />
                      <span className="text-gray-700">{doc}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Apply Card */}
              <div className="bg-[#2A9D8F] text-white rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Ready to Apply?</h3>
                <p className="text-white/80 mb-6">
                  Get started with your {product.title} application today. Our team is here to help you.
                </p>
                <Link
                  to="/contact"
                  className="block w-full bg-white text-[#2A9D8F] text-center py-3 rounded font-semibold hover:bg-gray-100 transition-colors"
                >
                  Apply Now
                </Link>
              </div>

              {/* Contact Card */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Need Help?</h3>
                <div className="space-y-4">
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="flex items-center space-x-3 text-gray-600 hover:text-[#2A9D8F]"
                  >
                    <div className="p-2 bg-[#2A9D8F]/10 rounded-full">
                      <Phone size={18} className="text-[#2A9D8F]" />
                    </div>
                    <span>{companyInfo.phone}</span>
                  </a>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center space-x-3 text-gray-600 hover:text-[#2A9D8F]"
                  >
                    <div className="p-2 bg-[#2A9D8F]/10 rounded-full">
                      <Mail size={18} className="text-[#2A9D8F]" />
                    </div>
                    <span>{companyInfo.email}</span>
                  </a>
                </div>
              </div>

              {/* Download Brochure */}
              {product.hasApplication && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Downloads</h3>
                  <button className="w-full flex items-center justify-center space-x-2 border-2 border-[#2A9D8F] text-[#2A9D8F] py-3 rounded font-medium hover:bg-[#2A9D8F] hover:text-white transition-colors">
                    <Download size={18} />
                    <span>Download Application</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <section className="mt-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Products</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedProducts.map((relatedProduct) => (
                  <Link
                    key={relatedProduct.id}
                    to={`/products/${relatedProduct.slug}`}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow group"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-gray-500">{relatedProduct.category}</span>
                      <h3 className="font-semibold text-gray-900 group-hover:text-[#2A9D8F] transition-colors">
                        {relatedProduct.title}
                      </h3>
                    </div>
                    <div className="h-1" style={{ backgroundColor: relatedProduct.color }} />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Loan Application Modal */}
      <LoanApplicationModal
        isOpen={showApplicationModal}
        onClose={() => setShowApplicationModal(false)}
        product={product}
      />

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
